import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

const DAY_STYLES = {
  none: { bg: '#fff', border: '#e2e8f0', color: '#94a3b8' },
  checkin: { bg: '#ecfdf5', border: '#6ee7b7', color: '#047857' },
  holiday: { bg: '#fef3c7', border: '#fcd34d', color: '#b45309' },
  both: { bg: 'linear-gradient(135deg, #ecfdf5 50%, #fef3c7 50%)', border: '#14b8a6', color: '#0f766e' },
  before_joining: { bg: '#f1f5f9', border: '#e2e8f0', color: '#cbd5e1' }
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function StaffingHolidayCalendar({ token }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token || !API_URL) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/holidays/calendar?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load calendar');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, month, year]);

  useEffect(() => {
    load();
  }, [load]);

  const calendarCells = useMemo(() => {
    if (!data?.days?.length) return [];
    const firstDow = new Date(year, month - 1, 1).getDay();
    const blanks = Array.from({ length: firstDow }, (_, i) => ({ blank: true, key: `b-${i}` }));
    const days = data.days.map((d) => ({ ...d, key: d.date }));
    return [...blanks, ...days];
  }, [data, month, year]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  if (error) return <Alert severity="warning">{error}</Alert>;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          Holiday calendar — {data?.company_name || ''}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            <ChevronLeft />
          </IconButton>
          <TextField
            size="small"
            select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 88 }} />
          <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="Next month">
            <ChevronRight />
          </IconButton>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}

      {data?.summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Paid days: ${data.summary.paid_days}`} color="success" variant="outlined" />
          <Chip size="small" label={`Check-ins: ${data.summary.checkin_days}`} sx={{ bgcolor: '#ecfdf5', color: '#047857' }} />
          <Chip size="small" label={`Public holidays: ${data.summary.public_holidays}`} sx={{ bgcolor: '#fef3c7', color: '#b45309' }} />
        </Stack>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#fef3c7', border: '1px solid #fcd34d' }} />
          <Typography variant="caption">Public holiday (paid)</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#ecfdf5', border: '1px solid #6ee7b7' }} />
          <Typography variant="caption">Checked in</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, background: 'linear-gradient(135deg, #ecfdf5 50%, #fef3c7 50%)', border: '1px solid #14b8a6' }} />
          <Typography variant="caption">Holiday + check-in</Typography>
        </Stack>
      </Stack>

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#fafafa' }}>
        <Grid container columns={7}>
          {WEEKDAYS.map((w) => (
            <Grid item xs key={w} sx={{ py: 1, textAlign: 'center', bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {w}
              </Typography>
            </Grid>
          ))}
          {calendarCells.map((cell) => {
            if (cell.blank) {
              return <Grid item xs key={cell.key} sx={{ minHeight: 72, borderRight: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }} />;
            }
            const style = DAY_STYLES[cell.day_type] || DAY_STYLES.none;
            return (
              <Grid
                item
                xs
                key={cell.key}
                sx={{
                  minHeight: 72,
                  p: 0.75,
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  background: style.bg,
                  borderTop: cell.day_type !== 'none' && cell.day_type !== 'before_joining' ? `2px solid ${style.border}` : undefined
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: style.color }}>
                  {cell.day}
                </Typography>
                {cell.holiday_name && (
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.2, color: '#b45309', mt: 0.25 }} noWrap title={cell.holiday_name}>
                    {cell.holiday_name}
                  </Typography>
                )}
                {cell.day_type === 'checkin' && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#047857' }}>
                    ✓ In
                  </Typography>
                )}
                {cell.day_type === 'both' && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#047857' }}>
                    ✓ + holiday
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {data?.joining_date && (
        <Typography variant="caption" color="text.secondary">
          Joining date: {data.joining_date}. Days before joining are not counted as paid.
        </Typography>
      )}
    </Stack>
  );
}
